import { useCallback, useEffect, useMemo, useState } from "react";

import type { BuyerListingAssociationRowDto } from "@/api/buyers";
import { MAX_PRESENT_PER_ACTION } from "@/lib/buyerSubmissionBulkRules";
import { listingShortNameOrFallback } from "@/lib/listingDisplay";

import { associationsCopy as copy } from "./associationsCopy";

/**
 * The listings ticked for PRESENT ALL SELECTED on ONE buyer's Associated
 * listings tab (D13).
 *
 * <p>🔴 <b>"Presented" is `submissionId != null`, and nothing else.</b> The
 * server's association row already carries the buyer's standing non-deleted
 * submission for that listing, archived included — that is the shared rule, so
 * it is read here, never re-derived. Only a row without one can be ticked.</p>
 *
 * <p>Ticks are keyed by LISTING id (the bulk request takes listing ids, and a
 * listing is on one buyer's list at most once) and survive a page change and a
 * search — the reader is assembling a set across the whole list. A tick whose
 * row has since become presented, or has left the list, is DROPPED: the bar and
 * the request never count it. While the confirm is open the drop is held, so
 * the confirm can name what it is leaving out rather than the list quietly
 * shrinking under the reader.</p>
 */

/** The name a row shows — the same short name the listing cell renders. */
export function associationListingName(row: BuyerListingAssociationRowDto): string {
  return listingShortNameOrFallback(row.listingDisplayName, row.listingBusinessName);
}

export function isPresented(row: BuyerListingAssociationRowDto): boolean {
  return Boolean(row.submissionId);
}

/** listingId → the name the row had when it was ticked. */
export type AssociationTicks = ReadonlyMap<string, string>;

export interface AssociationPick {
  listingId: string;
  name: string;
}

export interface AssociationLeftOut extends AssociationPick {
  reason: string;
}

/**
 * Splits the ticks against the rows as they are now: what will be presented,
 * and what will not and why.
 */
export function splitTicks(
  ticks: AssociationTicks,
  rows: readonly BuyerListingAssociationRowDto[],
): { eligible: AssociationPick[]; leftOut: AssociationLeftOut[] } {
  const byListing = new Map(rows.map((r) => [r.listingId, r]));
  const eligible: AssociationPick[] = [];
  const leftOut: AssociationLeftOut[] = [];
  for (const [listingId, tickedName] of ticks) {
    const row = byListing.get(listingId);
    if (!row) leftOut.push({ listingId, name: tickedName, reason: copy.noLongerOnList });
    else if (isPresented(row))
      leftOut.push({ listingId, name: associationListingName(row), reason: copy.alreadyPresented });
    else eligible.push({ listingId, name: associationListingName(row) });
  }
  return { eligible, leftOut };
}

/**
 * Drops every tick whose row is presented or gone.
 *
 * <p>🔴 <b>Returns `prev` UNCHANGED when nothing is dropped — the identity is the
 * contract.</b> It runs from an effect that sets state; a new map every time
 * would never let React bail out (see `combinedIntroSelection.refreshPicks`).</p>
 */
export function pruneTicks(
  prev: AssociationTicks,
  rows: readonly BuyerListingAssociationRowDto[],
): AssociationTicks {
  if (prev.size === 0) return prev;
  const { leftOut } = splitTicks(prev, rows);
  if (leftOut.length === 0) return prev;
  const next = new Map(prev);
  for (const l of leftOut) next.delete(l.listingId);
  return next;
}

export interface AssociationSelection {
  /** What PRESENT ALL SELECTED would send, in tick order. */
  eligible: AssociationPick[];
  /** Ticks that will not be sent, with the reason. Empty outside the confirm. */
  leftOut: AssociationLeftOut[];
  count: number;
  isTicked: (listingId: string) => boolean;
  toggle: (row: BuyerListingAssociationRowDto) => void;
  /** The header box's state for the rows on the current page. */
  page: { selectable: number; all: boolean; some: boolean; toggleAll: () => void };
  /** True when a counted tick is on a row the reader cannot see right now. */
  notAllOnThisPage: boolean;
  /** Why PRESENT ALL SELECTED is unavailable, or null when it is. */
  blockedReason: string | null;
  drop: (listingIds: readonly string[]) => void;
  clear: () => void;
}

export function useAssociationSelection({
  buyerLeadId,
  rows,
  pageListingIds,
  hold,
}: {
  buyerLeadId: string;
  /** The whole list — must be referentially stable across renders (the query's data). */
  rows: readonly BuyerListingAssociationRowDto[];
  /** The listing ids on the table's current page, in order. */
  pageListingIds: readonly string[];
  /** True while the confirm is open: ticks are not dropped under it. */
  hold: boolean;
}): AssociationSelection {
  const [ticks, setTicks] = useState<AssociationTicks>(() => new Map());

  // Leaving the buyer clears it — during render, so the old buyer's count never
  // paints for the new one.
  const [forBuyer, setForBuyer] = useState(buyerLeadId);
  if (forBuyer !== buyerLeadId) {
    setForBuyer(buyerLeadId);
    setTicks(new Map());
  }

  useEffect(() => {
    if (!hold) setTicks((prev) => pruneTicks(prev, rows));
  }, [rows, hold]);

  const { eligible, leftOut } = useMemo(() => splitTicks(ticks, rows), [ticks, rows]);

  const pageSelectable = useMemo(() => {
    const byListing = new Map(rows.map((r) => [r.listingId, r]));
    return pageListingIds.filter((id) => {
      const row = byListing.get(id);
      return row != null && !isPresented(row);
    });
  }, [rows, pageListingIds]);

  const rowNames = useMemo(
    () => new Map(rows.map((r) => [r.listingId, associationListingName(r)])),
    [rows],
  );

  const toggle = useCallback((row: BuyerListingAssociationRowDto) => {
    setTicks((prev) => {
      const next = new Map(prev);
      if (next.has(row.listingId)) next.delete(row.listingId);
      else if (!isPresented(row)) next.set(row.listingId, associationListingName(row));
      else return prev;
      return next;
    });
  }, []);

  const allOnPage = pageSelectable.length > 0 && pageSelectable.every((id) => ticks.has(id));
  const someOnPage = pageSelectable.some((id) => ticks.has(id));

  const toggleAll = useCallback(() => {
    setTicks((prev) => {
      const next = new Map(prev);
      const every = pageSelectable.every((id) => prev.has(id));
      for (const id of pageSelectable) {
        if (every) next.delete(id);
        else if (!next.has(id)) next.set(id, rowNames.get(id) ?? id);
      }
      return next;
    });
  }, [pageSelectable, rowNames]);

  const drop = useCallback((listingIds: readonly string[]) => {
    setTicks((prev) => {
      if (!listingIds.some((id) => prev.has(id))) return prev;
      const next = new Map(prev);
      for (const id of listingIds) next.delete(id);
      return next;
    });
  }, []);

  const clear = useCallback(() => setTicks(new Map()), []);

  const onPage = new Set(pageListingIds);
  const count = eligible.length;

  return {
    eligible,
    leftOut,
    count,
    isTicked: (id) => ticks.has(id),
    toggle,
    page: { selectable: pageSelectable.length, all: allOnPage, some: someOnPage, toggleAll },
    notAllOnThisPage: eligible.some((e) => !onPage.has(e.listingId)),
    blockedReason: count > MAX_PRESENT_PER_ACTION ? copy.tooMany : null,
    drop,
    clear,
  };
}
